import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Unlock, AlertTriangle, Skull } from 'lucide-react';

/**
 * QuizIntrigueWidget — Suspense / intrigue widget shown on the first 3 screens.
 *
 * Goal: keep the user hooked. Evolves in 5 stages:
 *   stage 0 = locked teaser (no key data yet)
 *   stage 1 = CA entered → "1 indicateur déverrouillé"
 *   stage 2 = food cost entered → "PERTE DÉTECTÉE" (live ticking counter)
 *   stage 3 = masse salariale or loyer entered → add tags + bump the loss
 *   stage 4 = problem chosen → full reveal "RÉCAP ANNUEL: X €" with red skull
 *
 * Props:
 *   - answers: full answers object
 *   - currentScreen: 0-based screen index
 *   - screenId: id of the active screen
 */

const formatEUR = (n) => {
  if (!Number.isFinite(n) || n <= 0) return '0';
  return Math.round(n).toLocaleString('fr-FR');
};

const computeLoss = (a) => {
  const ca = Number(a.ca_mensuel) || 0;
  const mp = Number(a.matieres_premieres_mensuel) || 0;
  const ms = Number(a.masse_salariale_mensuelle) || 0;
  const foodCost = ca > 0 ? (mp / ca) * 100 : 0;
  const masseSalPct = ca > 0 ? (ms / ca) * 100 : 0;

  let monthly = 0;
  const tags = [];

  if (foodCost > 30 && ca > 0) {
    const excess = ((foodCost - 28) / 100) * ca;
    if (excess > 0) {
      monthly += excess;
      tags.push({ label: `Food cost ${foodCost.toFixed(1)}%`, loss: Math.round(excess), color: '#EF4444' });
    }
  }
  if (masseSalPct > 35 && ca > 0) {
    const excess = ((masseSalPct - 32) / 100) * ca;
    if (excess > 0) {
      monthly += excess;
      tags.push({ label: `Masse sal. ${masseSalPct.toFixed(1)}%`, loss: Math.round(excess), color: '#F59E0B' });
    }
  }

  // Problem-based add-ons (Screen 3) — flat estimated drag per category
  const problemCosts = {
    marge_faible: 800,
    marketing: 1200,
    manque_clients: 1800,
    equipe: 600,
    organisation: 500,
  };
  const labels = {
    marge_faible: 'Marge faible',
    marketing: 'Marketing absent',
    manque_clients: 'Manque de clients',
    equipe: 'Équipe difficile',
    organisation: 'Organisation floue',
  };
  // plus_gros_probleme is now an array (multi-select); fallback to string for backwards compat
  const problems = Array.isArray(a.plus_gros_probleme)
    ? a.plus_gros_probleme
    : (a.plus_gros_probleme ? [a.plus_gros_probleme] : []);
  problems.forEach((p) => {
    if (problemCosts[p]) {
      monthly += problemCosts[p];
      tags.push({ label: labels[p], loss: problemCosts[p], color: '#EC4899' });
    }
  });

  return { monthly, annual: monthly * 12, tags, foodCost, masseSalPct, ca };
};

// Animated counter that ticks up smoothly
const TickingCounter = ({ value, duration = 1.0, prefix = '', suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(value) || value <= 0) {
      setDisplay(0);
      return;
    }
    const start = performance.now();
    const from = display;
    const to = value;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{prefix}{formatEUR(display)}{suffix}</>;
};

const QuizIntrigueWidget = ({ answers, currentScreen, screenId }) => {
  const { monthly, annual, tags, ca } = computeLoss(answers || {});

  // Determine stage
  let stage = 0;
  if (ca > 0) stage = 1;
  if (answers?.matieres_premieres_mensuel) stage = 2;
  if (answers?.masse_salariale_mensuelle || answers?.loyer_mensuel) stage = 3;
  const hasProblem = Array.isArray(answers?.plus_gros_probleme)
    ? answers.plus_gros_probleme.length > 0
    : !!answers?.plus_gros_probleme;
  if (hasProblem) stage = 4;

  // Only show on screens 0-3 (restaurant / finances / problemes / contact teaser)
  // After screen 4, we hide it (user has enough hooks at that point)
  if (currentScreen > 3) return null;
  // Don't render on ouverture branch screens (different flow)
  if (typeof screenId === 'string' && screenId.startsWith('ouverture_')) return null;

  // STAGE 0 — locked teaser
  if (stage === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-3 rounded-2xl overflow-hidden relative"
        data-testid="quiz-intrigue-widget"
        data-stage="0"
        style={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2A1810 100%)',
          border: '1px solid rgba(240,90,26,0.25)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* shimmer */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)' }}
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
        />
        <div className="relative px-4 py-3 flex items-center gap-3">
          <motion.div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(240,90,26,0.15)', border: '1px solid rgba(240,90,26,0.35)' }}
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Lock className="w-4 h-4" style={{ color: '#F05A1A' }} strokeWidth={2.5} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-black text-[12px] leading-tight">
              Ton audit personnalisé se construit
            </p>
            <p className="text-white/55 text-[10px] leading-tight mt-0.5">
              Continue · chaque réponse rend l'audit plus précis
            </p>
          </div>
          {/* 4-dot lock indicator */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {[0, 1, 2, 3].map((i) => (
              <motion.span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: '#F05A1A' }}
                animate={{ opacity: [0.2, 1, 0.2] }}
                transition={{ duration: 1.6, delay: i * 0.2, repeat: Infinity }}
              />
            ))}
          </div>
        </div>
      </motion.div>
    );
  }

  // STAGE 1 — CA entered, no losses yet
  if (stage === 1) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-3 rounded-2xl overflow-hidden relative"
        data-testid="quiz-intrigue-widget"
        data-stage="1"
        style={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #1F2937 100%)',
          border: '1px solid rgba(34,197,94,0.35)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3), 0 0 18px rgba(34,197,94,0.15)',
        }}
      >
        <div className="px-4 py-3 flex items-center gap-3">
          <motion.div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.45)' }}
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 16 }}
          >
            <Unlock className="w-4 h-4" style={{ color: '#22C55E' }} strokeWidth={2.5} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-black text-[12px] leading-tight">
              <span style={{ color: '#22C55E' }}>1 indicateur déverrouillé</span> · CA mensuel
            </p>
            <p className="text-white/55 text-[10px] leading-tight mt-0.5">
              Audit en cours sur <strong className="text-white">{formatEUR(ca)}€</strong> · entre tes coûts pour affiner ton diagnostic
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // STAGE 2, 3, 4 — losses revealed
  const isMaxStage = stage === 4;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 220, damping: 20 }}
      className="mt-3 rounded-2xl overflow-hidden relative"
      data-testid="quiz-intrigue-widget"
      data-stage={stage}
      style={{
        background: isMaxStage
          ? 'linear-gradient(135deg, #1A1A1A 0%, #3F0F0F 100%)'
          : 'linear-gradient(135deg, #1A1A1A 0%, #2A1810 100%)',
        border: `1px solid ${isMaxStage ? 'rgba(239,68,68,0.55)' : 'rgba(239,68,68,0.35)'}`,
        boxShadow: isMaxStage
          ? '0 8px 26px rgba(0,0,0,0.4), 0 0 30px rgba(239,68,68,0.45)'
          : '0 8px 24px rgba(0,0,0,0.3), 0 0 22px rgba(239,68,68,0.22)',
      }}
    >
      {/* Pulsing red border when stage >= 4 */}
      {isMaxStage && (
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          animate={{ opacity: [0, 0.35, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          style={{ boxShadow: '0 0 24px rgba(239,68,68,0.6) inset' }}
        />
      )}

      <div className="px-4 py-3.5">
        {/* Header row */}
        <div className="flex items-center gap-2.5 mb-2">
          <motion.div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: isMaxStage ? 'rgba(239,68,68,0.22)' : 'rgba(239,68,68,0.15)',
              border: `1px solid ${isMaxStage ? 'rgba(239,68,68,0.55)' : 'rgba(239,68,68,0.35)'}`,
            }}
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            {isMaxStage ? (
              <Skull className="w-4 h-4" style={{ color: '#EF4444' }} strokeWidth={2.4} />
            ) : (
              <AlertTriangle className="w-4 h-4" style={{ color: '#EF4444' }} strokeWidth={2.6} />
            )}
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-[10px] tracking-[3px] uppercase" style={{ color: '#EF4444' }}>
              {isMaxStage ? '💀 Récap annuel · Ce que ça te coûte' : '🚨 Audit en direct · Perte détectée'}
            </p>
            <p className="text-white/55 text-[10px] leading-tight mt-0.5">
              {isMaxStage
                ? 'Ton audit complet t\'attend à la fin · gratuit, sans pitch'
                : 'On affine ton audit au fil des réponses'}
            </p>
          </div>
        </div>

        {/* Big number */}
        <AnimatePresence mode="wait">
          <motion.p
            key={`loss-${monthly}-${isMaxStage}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="font-bebas leading-none tracking-tight"
            style={{
              color: '#EF4444',
              fontSize: 'clamp(28px, 7vw, 44px)',
              textShadow: isMaxStage ? '0 0 22px rgba(239,68,68,0.5)' : '0 0 12px rgba(239,68,68,0.35)',
            }}
          >
            −<TickingCounter value={isMaxStage ? annual : monthly} suffix="€" />
            <span className="text-white/40 text-xs font-bold uppercase tracking-widest ml-2">
              /{isMaxStage ? 'an' : 'mois'}
            </span>
          </motion.p>
        </AnimatePresence>

        {/* Tag breakdown */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="quiz-intrigue-tags">
            {tags.map((t, i) => (
              <motion.span
                key={`${t.label}-${i}`}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.08 }}
                className="text-[9px] font-black tracking-wider uppercase px-2 py-1 rounded-full"
                style={{
                  background: `${t.color}1F`,
                  color: t.color,
                  border: `1px solid ${t.color}55`,
                }}
              >
                {t.label} · −{formatEUR(t.loss)}€
              </motion.span>
            ))}
          </div>
        )}

        {/* CTA hint (only on max stage to push completion) */}
        {isMaxStage && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-white/65 text-[10px] mt-2.5 italic"
          >
            ↓ Termine ton audit pour voir <strong className="text-white">le détail complet et le plan d'action</strong>
          </motion.p>
        )}
      </div>
    </motion.div>
  );
};

export default QuizIntrigueWidget;
